<template>
  <div
    class="codelos-kanban-board-wrapper"
    ref="kanbanContainer"
    :style="kanbanContainerStyle"
  >
    <div
      class="codelos-dragged-task-clone-container"
      id="dragged-task-clone-container"
    ></div>
    <div class="codelos-kanban-content">
      <KanbanHeader
        :headerItems="getHeaderItems"
        :groupedTasks="groupedTasks"
        :canAddItem="canAddItem"
        :statusFieldRequired="statusFieldRequired"
        @onAddNewItem="
          (e) => $emit('onAddNewItem', { key: groupKey, value: e })
        "
      />
      <div class="codelos-kanban-column-wrapper">
        <KanbanColumn
          v-for="(column, columnIndex) in getHeaderItems"
          :key="`${column.value}-${columnIndex}`"
          :uniqueColumnKey="`${column.value}-${columnIndex}`"
          :isFirstColumn="columnIndex === 0"
          :headerItem="column"
          :isNoStatusColumn="column.value === 'nostatus'"
          :columnTasks="groupedTasks[column.value]"
          :keysToRender="getKeysToRender"
          :dragContext="dragContext"
          :groupKey="groupKey"
          :database="database"
          :actionFlowList="actionFlowList"
          :isDatabaseDropDownLoading="isDatabaseDropDownLoading"
          :actionDropdownApiLoading="actionDropdownApiLoading"
          :isItemSelected="isItemSelected"
          :statusFieldKey="statusFieldKey"
          :canAddItem="canAddItem"
          :statusFieldRequired="statusFieldRequired"
          @onDragStart="onDragStart"
          @onDrop="onDrop"
          @onForbiddenDrop="handleForbiddenDrop"
          @onAddNewItem="
            $emit('onAddNewItem', { key: groupKey, value: column.value })
          "
          @onActionTrigger="(e) => $emit('onActionTrigger', e)"
          @onDragEnd="clearScrollInterval"
        />
      </div>
    </div>
  </div>
</template>
<script>
import { updateDatabaseItemKey } from '../../services/userTable';
import KanbanColumn from './KanbanColumn.vue';
import KanbanHeader from './KanbanHeader.vue';

export default {
  name: 'KanbanBoard',
  props: {
    dataSchema: { type: Array, default: () => [] },
    tasks: { type: Array, default: () => [] },
    groupKey: { type: String, default: 'status' },
    getElementSettings: { type: Function, default: () => {} },
    database: { type: Object, required: true },
    actionFlowList: { type: Array, default: () => [] },
    isDatabaseDropDownLoading: { type: Function, default: () => {} },
    actionDropdownApiLoading: { type: Boolean, default: false },
    isItemSelected: { type: Function, default: () => {} },
    showToaster: { type: Function, default: () => {} },
    statusFieldKey: { type: String, default: 'status' },
    statusFieldRequired: { type: Boolean, default: false },
  },
  data() {
    return {
      dragContext: {
        currentCardId: null,
        isDragging: false,
        activeDropForbiddenId: null,
        activeDropForbiddenMessage: null,
      },
      autoScroll: {
        speed: 10,
        interval: null,
      },
      boardMaxHeight: null,
      boardHeightFrame: null,
      noStatusFallbackValue: 'Kein Status',
    };
  },
  methods: {
    isNoStatusValue(value) {
      return (
        value == null ||
        value === '' ||
        value === 'nostatus' ||
        value === this.noStatusFallbackValue
      );
    },
    getStatusOptions() {
      const keyItem =
        this.dataSchema.find((e) => e.name == this.groupKey) ||
        this.dataSchema.find((e) => e.type == 'status');

      return (keyItem?.options || [])
        .map((option) => ({
          value: option?.value ?? option?.label,
          label: option?.label ?? option?.value,
        }))
        .filter((option) => option.value || option.label);
    },
    getCanonicalStatusValue(value) {
      const matchedOption = this.getStatusOptions().find((option) => {
        return (
          String(option.value) === String(value) ||
          String(option.label) === String(value)
        );
      });

      return matchedOption?.value ?? null;
    },
    isKnownStatusValue(value) {
      if (this.isNoStatusValue(value)) {
        return true;
      }

      return this.getCanonicalStatusValue(value) !== null;
    },
    getColumnKey(value) {
      if (this.isNoStatusValue(value)) {
        return 'nostatus';
      }

      return this.getCanonicalStatusValue(value) || 'nostatus';
    },
    normalizeGroupValue(value) {
      return this.getColumnKey(value);
    },
    getDbGroupValue(value) {
      return value === 'nostatus' ? '' : value;
    },
    setForbiddenDropTarget(
      dropTargetId,
      message = 'Status ist ein Pflichtfeld'
    ) {
      this.$set(this.dragContext, 'activeDropForbiddenId', dropTargetId);
      this.$set(this.dragContext, 'activeDropForbiddenMessage', message);
      this.$set(this.dragContext, 'activeDropTargetId', null);
      this.$set(this.dragContext, 'activeDropPosition', null);
    },
    clearDropTargetHints() {
      this.$set(this.dragContext, 'activeDropForbiddenId', null);
      this.$set(this.dragContext, 'activeDropForbiddenMessage', null);
      this.$set(this.dragContext, 'activeDropTargetId', null);
      this.$set(this.dragContext, 'activeDropPosition', null);
    },
    handleForbiddenDrop(message) {
      const fallbackMessage = 'Status ist ein Pflichtfeld';
      this.showToaster(message || fallbackMessage, 'Fehlerhaft !', 'danger');
      this.clearDropTargetHints();
    },
    getGroupedTasks() {
      const taskMap = {
        nostatus: [],
      };
      this.getHeaderItems.forEach((el) => {
        taskMap[el.value] = [];
      });
      for (const task of this.tasks) {
        const groupValue = this.normalizeGroupValue(task[this.groupKey]);

        if (!('codelosKanbanOrder' in task)) {
          task['codelosKanbanOrder'] = Infinity;
        }

        if (!(groupValue in taskMap)) {
          taskMap[groupValue] = [];
        }

        taskMap[groupValue].push(task);
      }

      for (const groupKey in taskMap) {
        taskMap[groupKey].sort(
          (a, b) => a.codelosKanbanOrder - b.codelosKanbanOrder
        );
      }

      return taskMap;
    },
    setDragContext(key, value) {
      this.dragContext[key] = value;
    },
    onDragStart(id) {
      this.dragContext.isDragging = true;
      this.dragContext.currentCardId = id;
      this.clearDropTargetHints();
    },
    handleDragOverScroll(e) {
      if (this.dragContext.isDragging) {
        e.preventDefault();
        const container = this.$refs.kanbanContainer;
        if (container) {
          const rect = container.getBoundingClientRect();
          const buffer = 100;

          let dx = 0;
          let dy = 0;

          if (e.clientX + buffer > rect.right) {
            dx = this.autoScroll.speed;
          } else if (e.clientX < rect.left + buffer) {
            dx = -this.autoScroll.speed;
          }

          if (e.clientY + buffer > rect.bottom) {
            dy = this.autoScroll.speed;
          } else if (e.clientY < rect.top + buffer) {
            dy = -this.autoScroll.speed;
          }

          if (dx !== 0 || dy !== 0) {
            if (!this.autoScroll.interval) {
              this.autoScroll.interval = setInterval(() => {
                container.scrollLeft += dx;
                container.scrollTop += dy;
              }, 16);
            }
          } else {
            this.clearScrollInterval();
          }
        }
      }
    },
    clearScrollInterval() {
      if (this.autoScroll.interval) {
        clearInterval(this.autoScroll.interval);
        this.autoScroll.interval = null;
      }
    },
    scheduleBoardHeightUpdate() {
      if (typeof window === 'undefined') return;

      if (this.boardHeightFrame) {
        window.cancelAnimationFrame(this.boardHeightFrame);
      }

      this.boardHeightFrame = window.requestAnimationFrame(() => {
        this.boardHeightFrame = null;
        this.updateBoardHeight();
      });
    },
    updateBoardHeight() {
      const container = this.$refs.kanbanContainer;
      if (!container || typeof window === 'undefined') return;

      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;
      const bottomGap = 16;
      const availableHeight =
        viewportHeight - container.getBoundingClientRect().top - bottomGap;
      const nextHeight = `${Math.max(160, Math.floor(availableHeight))}px`;

      if (this.boardMaxHeight !== nextHeight) {
        this.boardMaxHeight = nextHeight;
      }
    },
    async onDrop({ dropPosition, dropTaskIndex, task }) {
      try {
        this.clearScrollInterval();
        const draggedId = this.dragContext.currentCardId;
        this.dragContext.isDragging = false;
        this.dragContext.currentCardId = null;

        if (draggedId === null) return;

        const currentTask = this.tasks.find((el) => el.id == draggedId);
        if (!currentTask) return;

        const fromColumnKey = this.getColumnKey(currentTask[this.groupKey]);
        const toColumnKey = this.getColumnKey(task[this.groupKey]);

        if (toColumnKey === 'nostatus' && this.statusFieldRequired) {
          this.clearDropTargetHints();
          return;
        }

        const sameColumn = fromColumnKey === toColumnKey;

        const groupedTasks = this.getGroupedTasks();
        const fromColumnTasks = groupedTasks[fromColumnKey] || [];
        const toColumnTasks = groupedTasks[toColumnKey] || [];
        const rawIndex = Math.max(
          0,
          Math.min(dropTaskIndex, toColumnTasks.length - 1)
        );
        let insertIndex = dropPosition === 'after' ? rawIndex + 1 : rawIndex;

        const taskIndexInFromColumn = fromColumnTasks.findIndex(
          (t) => t.id == currentTask.id
        );
        if (taskIndexInFromColumn === -1) return;

        if (sameColumn) {
          const [movedTask] = fromColumnTasks.splice(taskIndexInFromColumn, 1);

          if (insertIndex > taskIndexInFromColumn) insertIndex--;

          if (taskIndexInFromColumn === insertIndex) {
            fromColumnTasks.splice(taskIndexInFromColumn, 0, movedTask); // restore
            return;
          }

          toColumnTasks.splice(insertIndex, 0, movedTask);
        } else {
          const [movedTask] = fromColumnTasks.splice(taskIndexInFromColumn, 1);
          movedTask[this.groupKey] = toColumnKey;
          toColumnTasks.splice(insertIndex, 0, movedTask);
        }

        toColumnTasks.forEach((t, i) => (t.codelosKanbanOrder = i));
        if (!sameColumn) {
          fromColumnTasks.forEach((t, i) => (t.codelosKanbanOrder = i));
        }

        if (sameColumn) {
          await Promise.allSettled(
            fromColumnTasks.map((item) => {
              const payload = { value: item.codelosKanbanOrder };
              return updateDatabaseItemKey(
                this.database.name,
                item.id,
                'codelosKanbanOrder',
                payload
              );
            })
          );
        } else {
          const dbValue = this.getDbGroupValue(toColumnKey);
          await updateDatabaseItemKey(
            this.database.name,
            currentTask.id,
            this.groupKey,
            { value: dbValue }
          );

          await Promise.allSettled(
            [...fromColumnTasks, ...toColumnTasks].map((item) => {
              const payload = { value: item.codelosKanbanOrder };
              return updateDatabaseItemKey(
                this.database.name,
                item.id,
                'codelosKanbanOrder',
                payload
              );
            })
          );
        }
      } catch (e) {
        this.showToaster(e?.message || e, 'Fehlerhaft !', 'danger');
      } finally {
        this.clearDropTargetHints();
      }
    },
  },
  computed: {
    canAddItem() {
      return this.database.access_level !== 'read';
    },
    groupedTasks() {
      return this.getGroupedTasks();
    },
    getHeaderItems() {
      const keyItem =
        this.dataSchema.find((e) => e.name == this.groupKey) ||
        this.dataSchema.find((e) => e.type == 'status');
      const emptyElement = { value: 'nostatus', label: 'Kein Status' };

      if (!keyItem || !['radio', 'status'].includes(keyItem?.type)) {
        return [emptyElement];
      }

      const headerItems = (keyItem.options || []).map((e) => ({
        value: e?.value || e?.label,
        label: e?.label ?? e?.value,
      }));

      const showNoStatusColumn =
        !this.statusFieldRequired ||
        this.hasNoStatusTasks ||
        this.hasOrphanStatusTasks;

      return showNoStatusColumn ? [emptyElement, ...headerItems] : headerItems;
    },
    hasNoStatusTasks() {
      return this.tasks.some((task) =>
        this.isNoStatusValue(task?.[this.groupKey])
      );
    },
    hasOrphanStatusTasks() {
      return this.tasks.some((task) => {
        const value = task?.[this.groupKey];
        return !this.isNoStatusValue(value) && !this.isKnownStatusValue(value);
      });
    },
    getKeysToRender() {
      const filteredSchema = this.dataSchema.reduce((acc, curr) => {
        if (
          (curr.highlight || curr.settings?.highlight) &&
          !this.getElementSettings(curr, 'layoutItem')
        ) {
          acc.push({
            name: curr.name,
            type: curr.type,
            schema: curr.schema || [],
          });
        }
        return acc;
      }, []);
      return filteredSchema;
    },
    isKeinStatusInHeader() {
      return this.getHeaderItems.some(
        (item) => item.value === this.noStatusFallbackValue
      );
    },
    kanbanContainerStyle() {
      return this.boardMaxHeight ? { maxHeight: this.boardMaxHeight } : null;
    },
  },
  mounted() {
    document.addEventListener('dragover', this.handleDragOverScroll);
    window.addEventListener('resize', this.scheduleBoardHeightUpdate);
    this.scheduleBoardHeightUpdate();
  },
  updated() {
    this.scheduleBoardHeightUpdate();
  },
  beforeDestroy() {
    this.clearScrollInterval();
    document.removeEventListener('dragover', this.handleDragOverScroll);
    window.removeEventListener('resize', this.scheduleBoardHeightUpdate);

    if (this.boardHeightFrame) {
      window.cancelAnimationFrame(this.boardHeightFrame);
      this.boardHeightFrame = null;
    }
  },
  components: { KanbanHeader, KanbanColumn },
};
</script>
<style scoped>
.codelos-kanban-board-wrapper {
  width: 100%;
  overflow-x: auto;
  overflow-y: auto;
  max-height: calc(100dvh - 290px);
  padding: 0;
  position: relative;
  user-select: none;
  min-height: 0;
}

.codelos-kanban-content {
  min-width: 100%;
  width: max-content;
  padding-bottom: 12px;
}

.codelos-kanban-column-wrapper {
  display: flex;
  gap: 36px;
  align-items: stretch;
}

.codelos-kanban-board-wrapper .codelos-dragged-task-clone-container {
  height: 0px;
  width: 0px;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
}
</style>
